import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { Flex, Heading, Text, Box, Spinner, useToast, Center, Stack, Link } from '@chakra-ui/react';
import { FaBookReader } from 'react-icons/fa';
import SelectedGameContext from '../context/SelectedGameContext';
import ProfileContext, {
    getProfileErrorText,
    getProfileErrorHeader,
} from '../context/ProfileContext';
import config from '../config';
import DataCaller from '../util/dataCaller';
import { LeaderboardDetail } from '../interfaces/ILeaderboardConfig';
import LeaderboardConfigForm from '../forms/LeaderboardConfig';

const initialValues: LeaderboardDetail = {
    name: '',
    order: 'ascending',
    score_config: {
        capacity: 0,
        type: 'insertonly',
        has_data: false,
    },
    timeframes: {
        all: false,
        manual: undefined,
        daily: undefined,
        weekly: undefined,
        monthly: undefined,
        yearly: undefined,
    },
};

type AddLeaderboardConfigProps = {
    isSaved?: boolean;
};

export const AddLeaderboardConfig: React.FC<AddLeaderboardConfigProps> = ({ isSaved = false }) => {
    const selectedGame = useContext(SelectedGameContext);
    const currLicense = selectedGame?.currLicense;
    const profile = useContext(ProfileContext);
    const [isEditDataLoaded, setIsEditDataLoaded] = useState(!isSaved);
    const [editData, setEditData] = useState<LeaderboardDetail>(initialValues);
    const toast = useToast();
    const { keycloak } = useKeycloak();
    const url = `${config.leaderboardServiceURL}/game/${currLicense?.key}/leaderboard`;
    const { id } = useParams();
    const navigate = useNavigate();

    const renderError = () => (
        <Center width="100%" height="100%" display="flex" flexDirection="column">
            <Heading as="h3" size="md">
                {profile.errors.map((error) => getProfileErrorHeader(error))}
            </Heading>
            {profile.errors.map((error) => getProfileErrorText(error))}
        </Center>
    );

    const saveConfig = async (values: LeaderboardDetail) => {
        setIsEditDataLoaded(false);
        const dataCaller = new DataCaller(url, keycloak.token!);
        const res = await dataCaller.update(values!.name, values);

        if (res.status !== 200) {
            setIsEditDataLoaded(true);
            setEditData(values);
            return toast({
                title: 'There was an error',
                description: 'Please try creating/updating the Leaderboard config again.',
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
        setIsEditDataLoaded(true);
        toast({
            title: 'Success!',
            description: 'Leaderboard config has been created. Cheers!',
            status: 'success',
            duration: 9000,
            isClosable: true,
        });

        navigate(`/game/${currLicense?.key}/leaderboards`);
    };

    useEffect(() => {
        if (!currLicense) {
            return;
        }
        if (!id) {
            return;
        }
        const loadConfig = async (configName: string) => {
            const dataCaller = new DataCaller(url, keycloak.token!);
            const res = await dataCaller.get(configName);

            if (res.status !== 200) {
                setIsEditDataLoaded(true);
                if (res.status === 404) {
                    return;
                }
                toast({
                    title: 'Error',
                    description: res.message,
                    status: 'error',
                    duration: 9000,
                    isClosable: true,
                });
                return;
            }

            const { data: lb } = await res.json();
            setEditData(lb);
            setIsEditDataLoaded(true);
        };
        loadConfig(id!);
    }, [currLicense, id]);

    const handleSubmit = (values: LeaderboardDetail) => {
        saveConfig(values);
    };

    const displaySection = () => {
        if (!isEditDataLoaded) {
            return (
                <Center width="100%" height="100%">
                    <Spinner data-cy="loading" />
                </Center>
            );
        }
        return (
            <Box>
                <Flex justifyContent={'space-between'}>
                    <Heading> Leaderboard Configurations</Heading>
                    <Link
                        href={`${config.docsURL}/tutorials/leaderboard-configuration-manager`}
                        isExternal
                    >
                        <Flex alignItems={'center'}>
                            <Text mr={2}>Go to Documentation</Text>
                            <FaBookReader max={'2px'}></FaBookReader>
                        </Flex>
                    </Link>
                </Flex>
                <Stack spacing={10} direction="row">
                    {editData && (
                        <LeaderboardConfigForm
                            initialData={editData}
                            onSubmit={handleSubmit}
                            isSaved={isSaved}
                        />
                    )}
                </Stack>
            </Box>
        );
    };
    return (
        <Box>
            {profile.errors.length > 0 ? (
                <>
                    <Heading textAlign="center" marginBottom="1em">
                        Oh No! Error Detected
                    </Heading>
                    {renderError()}
                </>
            ) : isEditDataLoaded ? (
                displaySection()
            ) : (
                <Center width="100%" height="100%">
                    <Spinner data-cy="loading" />
                </Center>
            )}
            {!currLicense && profile.errors.length === 0 && (
                <Center width="100%" height="100%">
                    <Spinner data-cy="loading" />
                </Center>
            )}
        </Box>
    );
};
